import React, { useEffect, useState } from 'react';
import { X } from 'lucide-react';
import { dealApi } from '../services/dealApi';
import { ACQUISITION_LABEL, Acquisition, DealForm, SECURITY_KINDS, SecurityKind } from '../types/deal';
import { Company } from '../types/company';
import { Vehicle, KIND_LABEL } from '../types/vehicle';
import { SidePanel } from './SidePanel';

const EMPTY = (companyId: number | null = null, vehicleId: number | null = null): DealForm => ({
  vehicleId, companyId, securityKind: '', acquisition: 'new', investedOn: '', note: '', shares: null, pricePerShare: null,
});

/**
 * 투자 건 추가 — 2026-11-02 「투자 건」 메뉴를 투자처 안으로 합치며 페이지에서 떼어 냈다.
 * 투자처 상세 · 자금원 상세에서 연다(그 투자처 · 자금원이 골라진 채로). 오른쪽 패널(UI 규칙).
 */
export const DealFormModal: React.FC<{
  open: boolean; vehicles: Vehicle[]; companies: Company[];
  companyId?: number | null; vehicleId?: number | null;
  onClose: () => void; onSaved: () => void;
}> = ({ open, vehicles, companies, companyId = null, vehicleId = null, onClose, onSaved }) => {
  const [form, setForm] = useState<DealForm>(EMPTY);
  const [saving, setSaving] = useState(false);
  // 실패하면 창 안에 이유를 보인다 — 던지면 단추가 아무 반응 없이 멈춘다(실사용 테스트 7회차)
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => { if (open) setForm(EMPTY(companyId, vehicleId)); }, [open, companyId, vehicleId]);
  if (!open) return null;

  const set = <K extends keyof DealForm>(k: K, v: DealForm[K]) => setForm((f) => ({ ...f, [k]: v }));
  const input = 'w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:border-indigo-400 focus:outline-none';
  const label = 'block text-xs font-medium text-slate-500 mb-1';
  const can = form.vehicleId !== null && form.companyId !== null && !saving;

  const save = async () => {
    setSaving(true); setErr(null);
    try { await dealApi.create(form); onSaved(); } catch (e) { setErr((e as Error).message); } finally { setSaving(false); }
  };

  return (
    <SidePanel onClose={onClose} bare>
      <div className="flex flex-col min-h-full">
        <div className="flex items-start justify-between gap-4 px-5 py-4 border-b border-slate-100">
          <div>
            <h2 className="text-base font-bold text-slate-900">투자 건 추가</h2>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700"><X size={18} /></button>
        </div>
        <div className="px-5 py-4 space-y-3">
          <div>
            <label className={label} htmlFor="d-vehicle">자금원</label>
            <select id="d-vehicle" className={input} value={form.vehicleId ?? ''}
              onChange={(e) => set('vehicleId', e.target.value === '' ? null : Number(e.target.value))}>
              <option value="">선택</option>
              {vehicles.map((v) => (
                <option key={v.id} value={v.id}>
                  {v.name} ({KIND_LABEL[v.kind]})
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className={label} htmlFor="d-company">투자처</label>
            <select id="d-company" className={input} value={form.companyId ?? ''}
              onChange={(e) => set('companyId', e.target.value === '' ? null : Number(e.target.value))}>
              <option value="">선택</option>
              {companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={label} htmlFor="d-date">투자일</label>
              <input id="d-date" type="date" className={input} value={form.investedOn}
                onChange={(e) => set('investedOn', e.target.value)} />
            </div>
            <div>
              <label className={label} htmlFor="d-sec">증권</label>
              <select id="d-sec" className={input} value={form.securityKind}
                onChange={(e) => set('securityKind', e.target.value as SecurityKind | '')}>
                <option value="">미정</option>
                {SECURITY_KINDS.map((k) => <option key={k} value={k}>{k}</option>)}
              </select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={label} htmlFor="d-shares">취득 주식 수</label>
              <input id="d-shares" type="number" min={0} className={input + ' text-right'} value={form.shares ?? ''}
                onChange={(e) => set('shares', e.target.value === '' ? null : Number(e.target.value))} />
            </div>
            <div>
              <label className={label} htmlFor="d-price">주당 취득가 (원)</label>
              <input id="d-price" type="number" min={0} className={input + ' text-right'} value={form.pricePerShare ?? ''}
                onChange={(e) => set('pricePerShare', e.target.value === '' ? null : Number(e.target.value))} />
            </div>
          </div>
          {form.shares && form.pricePerShare ? (
            <p className="text-xs text-slate-500">취득가액 {(form.shares * form.pricePerShare).toLocaleString()}원 — 실제 돈은 원장 「투자」로 적습니다</p>
          ) : null}
          <div>
            <label className={label}>인수 방식</label>
            <div className="flex gap-2">
              {(['new', 'secondary'] as Acquisition[]).map((a) => (
                <button key={a} type="button" onClick={() => set('acquisition', a)}
                  className={`px-3 py-1.5 rounded-lg text-xs font-medium border ${form.acquisition === a ? 'bg-slate-900 border-slate-900 text-white' : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-100'}`}>
                  {ACQUISITION_LABEL[a]}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className={label} htmlFor="d-note">비고</label>
            <textarea id="d-note" className={input + ' h-16 resize-none'} value={form.note}
              onChange={(e) => set('note', e.target.value)} />
          </div>
        </div>
        <div className="mt-auto sticky bottom-0 bg-white flex items-center justify-end gap-2 px-5 py-4 border-t border-slate-100">
          {err && <p role="alert" className="mr-auto text-xs text-red-600">{err}</p>}
          <button onClick={onClose}
            className="px-4 py-2 rounded-lg text-sm font-medium border border-slate-300 text-slate-700 hover:bg-slate-50">취소</button>
          <button onClick={save} disabled={!can}
            className="px-5 py-2 rounded-lg text-sm font-medium bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-40">
            {saving ? '저장 중...' : '등록'}
          </button>
        </div>
      </div>
    </SidePanel>
  );
};
